// Permanent site badges — one employee can hold several (per customer, or
// per specific site under a customer). Once issued, a badge overrides the
// rolling drug-test-age check for badge_valid_days (default 365, set per
// customer under Customers → 📋 Reqs) — see services/crewCompliance.js.
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { styles, colors } from './UI';

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '5vh 16px', zIndex: 1000, overflowY: 'auto',
};
const card = (w) => ({
  background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: w,
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
});
const h2 = { margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#1F2937' };
const sub = { fontSize: 11, color: '#6B7280', marginBottom: 14 };

const fmtDate = (s) => s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const addDays = (iso, days) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export function EmployeeBadgesModal({ employee, onClose, onError }) {
  // Deactivated employees are sealed read-only server-side (workforce.js
  // assertEmployeeUnsealed on the badge add/delete routes).
  const sealed = !employee.is_active;
  const [badges, setBadges] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);

  const [custQuery, setCustQuery] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [sites, setSites] = useState([]);
  const [badgeValidDays, setBadgeValidDays] = useState(365);
  const [siteId, setSiteId] = useState('');
  const [issuedDate, setIssuedDate] = useState('');
  const [notes, setNotes] = useState('');

  const reload = () => api.listEmployeeBadges(employee.id).then(setBadges).catch(e => onError(e.message));
  useEffect(() => {
    reload();
    api.listCustomers({ status: 'A', limit: 5000 }).then(setCustomers).catch(e => onError(e.message));
  }, [employee.id]);

  const custName = customers.find(c => String(c.id) === String(customerId))?.name || '';
  const filteredCustomers = custQuery.trim()
    ? customers.filter(c => (c.name || '').toLowerCase().includes(custQuery.trim().toLowerCase())).slice(0, 30)
    : [];

  const pickCustomer = async (id) => {
    setCustomerId(id); setCustQuery(''); setSiteId(''); setSites([]); setBadgeValidDays(365);
    if (!id) return;
    try {
      const r = await api.getCustomerRequirements(id);
      setSites((r.sites || []).filter(s => s.is_active !== false));
      setBadgeValidDays((r.customer_requirements && r.customer_requirements.badge_valid_days) || 365);
    } catch (_) { /* sites/validity optional */ }
  };

  const add = async () => {
    if (!customerId || !issuedDate) { onError('Pick a customer and an issued date.'); return; }
    const days = Number(badgeValidDays);
    if (!Number.isFinite(days) || days <= 0) { onError('Valid Days must be a positive number.'); return; }
    setBusy(true);
    try {
      await api.createEmployeeBadge(employee.id, {
        customer_id: customerId, site_id: siteId || null, issued_date: issuedDate,
        valid_days: days, notes: notes.trim() || null,
      });
      setCustomerId(''); setSiteId(''); setIssuedDate(''); setNotes(''); setSites([]); setBadgeValidDays(365);
      await reload();
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!confirm(`Remove the badge for ${b.customer_name}${b.site_name ? ' — ' + b.site_name : ''}?`)) return;
    try { await api.deleteEmployeeBadge(employee.id, b.id); setBadges(prev => prev.filter(x => x.id !== b.id)); }
    catch (e) { onError(e.message); }
  };

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={card(560)}>
        <h2 style={h2}>🪪 Site Badges — {employee.full_name}</h2>
        <div style={sub}>
          A permanent badge, once issued, covers site access for its own valid-days window — drug test
          age no longer matters while the badge is active. Valid Days pre-fills from this customer's
          current default but is set per badge, so a later change to the customer's default never
          reshuffles a badge already issued. Scope to a specific site when the customer has more than
          one, or leave it customer-wide.
        </div>
        {sealed && (
          <div style={{ background: '#FEF2F2', border: '1px solid #EF4444', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991B1B', marginBottom: 10 }}>
            🔒 {employee.full_name} is deactivated — badges are sealed read-only. Reactivate on the Roster row to make changes.
          </div>
        )}

        {!badges ? <div style={{ fontSize: 12, color: '#6B7280' }}>Loading…</div> : (
          <>
            {badges.length === 0 && <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>No badges on file yet.</div>}
            {badges.map(b => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F3F4F6', fontSize: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{b.customer_name}{b.site_name ? ` — ${b.site_name}` : ' (customer-wide)'}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>
                    Issued {fmtDate(b.issued_date)} · valid {b.valid_days}d · expires {fmtDate(addDays(b.issued_date, b.valid_days))}
                    {b.notes ? ` · ${b.notes}` : ''}
                  </div>
                </div>
                {!sealed && <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 10px' }} onClick={() => remove(b)}>Remove</button>}
              </div>
            ))}
          </>
        )}

        {!sealed && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>+ Add Badge</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#6B7280', fontWeight: 700, marginBottom: 3 }}>Customer</div>
              {customerId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{custName}</span>
                  <button style={{ ...styles.btnSecondary, fontSize: 10, padding: '2px 6px' }} onClick={() => pickCustomer('')}>Change</button>
                </div>
              ) : (
                <>
                  <input style={styles.input} value={custQuery} onChange={e => setCustQuery(e.target.value)} placeholder="🔍 Search customer…" />
                  {filteredCustomers.length > 0 && (
                    <div style={{ position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, maxHeight: 200, overflowY: 'auto', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                      {filteredCustomers.map(c => (
                        <div key={c.id} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                          onClick={() => pickCustomer(c.id)}
                          onMouseDown={e => e.preventDefault()}>{c.name}</div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div>
              <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#6B7280', fontWeight: 700, marginBottom: 3 }}>Site</div>
              <select style={styles.input} value={siteId} onChange={e => setSiteId(e.target.value)} disabled={!customerId}>
                <option value="">— Customer-wide (all sites) —</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#6B7280', fontWeight: 700, marginBottom: 3 }}>Issued Date</div>
              <input type="date" style={styles.input} value={issuedDate} onChange={e => setIssuedDate(e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#6B7280', fontWeight: 700, marginBottom: 3 }}>Valid Days</div>
              <input type="number" min="1" style={styles.input} value={badgeValidDays}
                onChange={e => setBadgeValidDays(e.target.value)} disabled={!customerId}
                title="Pre-filled from this customer's current default — override for this one badge if it differs." />
            </div>
          </div>
          {customerId && issuedDate && Number(badgeValidDays) > 0 && (
            <div style={{ fontSize: 11, color: '#059669', marginTop: 6 }}>
              Valid {badgeValidDays} days — expires {fmtDate(addDays(issuedDate, Number(badgeValidDays)))}.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#6B7280', fontWeight: 700, marginBottom: 3 }}>Notes (optional)</div>
            <input style={styles.input} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. badge #, escort contact" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button style={styles.btnSecondary} onClick={onClose}>Close</button>
            <button style={styles.btn} disabled={busy || !customerId || !issuedDate || !(Number(badgeValidDays) > 0)} onClick={add}>{busy ? 'Adding…' : 'Add Badge'}</button>
          </div>
        </div>
        )}
        {sealed && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, paddingTop: 14, borderTop: '1px solid #E5E7EB' }}>
            <button style={styles.btnSecondary} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Bulk badge loader (2026-07-14) — scoped to ONE customer (the one already
// picked on Site Access). Drag-drop a spreadsheet of names + issued dates
// (+ optional site/valid-days/notes) → preview → commit. Same shape as the
// Roster names-only importer: template download, dry-run preview with
// per-row status, then a commit that writes everything that previewed clean.
export function BadgeBulkImport({ customerId, customerName, onImported }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setError(''); setResult(null); };

  const runPreview = async (f) => {
    if (!f) return;
    setError(''); setResult(null); setBusy(true); setFile(f);
    try { setPreview(await api.previewBadgeBulk(customerId, f)); }
    catch (e) { setError(e.message); setPreview(null); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const r = await api.commitBadgeBulk(customerId, file);
      setResult(r); setPreview(null); setFile(null);
      onImported?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) runPreview(f);
  };

  return (
    <div style={{ ...styles.card, marginBottom: 12 }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? colors.orange : '#CBD5E1'}`,
          background: dragOver ? '#FFF7ED' : '#F8FAFC',
          borderRadius: 8, padding: '18px 16px', textAlign: 'center', cursor: 'pointer',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: colors.charcoal }}>
          🪪 Bulk load badges for {customerName} — drag &amp; drop a spreadsheet here
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
          Name + issued date required; site, valid days, and notes are optional (blank valid days uses
          this customer's current default).
          {' '}{busy && !preview ? 'Reading…' : 'Click to browse.'}
        </div>
        <button
          style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', marginTop: 8 }}
          onClick={e => { e.stopPropagation(); api.downloadBadgeTemplate(customerId).catch(err => setError(err.message)); }}>
          ⬇ Download Template
        </button>
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) runPreview(f); }} />
      </div>

      {error && <div style={{ ...styles.errorBox, marginTop: 10 }}>{error}</div>}

      {preview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {file?.name ? <strong>{file.name}</strong> : null}
            <span style={{ color: '#6B7280' }}> · default valid days: {preview.default_valid_days}</span>
            <span style={{ color: '#6B7280' }}> · {preview.total_in_file} row{preview.total_in_file === 1 ? '' : 's'} found</span>
            {' · '}<span style={{ color: '#047857', fontWeight: 700 }}>{preview.ready_count} ready</span>
            {preview.employees_to_add > 0 &&
              <>{' · '}<span style={{ color: '#B45309', fontWeight: 700 }}>➕ {preview.employees_to_add} new to roster</span></>}
            {preview.error_count > 0 &&
              <>{' · '}<span style={{ color: '#DC2626', fontWeight: 700 }}>✗ {preview.error_count} error{preview.error_count === 1 ? '' : 's'}</span></>}
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6 }}>
            {[...preview.items].sort((a, b) => (a.error ? 0 : 1) - (b.error ? 0 : 1)).map((it, idx) => (
              <div key={idx}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', fontSize: 12,
                  borderTop: idx ? '1px solid #F1F5F9' : 'none',
                  background: it.error ? '#FEF2F2' : 'transparent',
                }}>
                <span style={{ width: 16, textAlign: 'center', color: it.error ? '#DC2626' : (it.match_status === 'new' ? '#047857' : '#374151') }}>
                  {it.error ? '✗' : (it.match_status === 'new' ? '＋' : '✓')}
                </span>
                <span style={{ fontWeight: 600, minWidth: 140 }}>{it.name}</span>
                <span style={{ color: '#6B7280', minWidth: 100 }}>{it.site_text || 'customer-wide'}</span>
                <span style={{ color: '#6B7280', minWidth: 90 }}>{it.issued_date || '—'}</span>
                <span style={{ color: '#6B7280', minWidth: 60 }}>{it.valid_days}d</span>
                {it.error
                  ? <span style={{ color: '#DC2626', marginLeft: 'auto' }}>{it.error}</span>
                  : it.match_status === 'new' && <span style={{ color: '#B45309', marginLeft: 'auto' }}>will be added to roster</span>}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <button style={styles.btn} onClick={commit} disabled={busy || preview.ready_count === 0}>
              {busy ? 'Loading…' : `Load ${preview.ready_count} badge${preview.ready_count === 1 ? '' : 's'}`}
            </button>
            <button style={styles.btnSecondary} onClick={reset} disabled={busy}>Cancel</button>
            {preview.error_count > 0 && (
              <span style={{ fontSize: 12, color: '#6B7280' }}>Red rows are skipped — fix the file and re-drop to include them.</span>
            )}
          </div>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 6, background: '#ECFDF5', border: '1px solid #A7F3D0', fontSize: 13 }}>
          ✓ Loaded <strong>{result.written}</strong> badge{result.written === 1 ? '' : 's'}
          {result.employees_added ? <> · added {result.employees_added} new employee{result.employees_added === 1 ? '' : 's'} to the roster ({result.added_names.slice(0, 5).join(', ')}{result.added_names.length > 5 ? ', …' : ''})</> : null}
          {result.error_count ? <> · skipped {result.error_count} row{result.error_count === 1 ? '' : 's'} with errors</> : null}.
          {' '}<button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', marginLeft: 6 }} onClick={reset}>Done</button>
        </div>
      )}
    </div>
  );
}
